import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import BeforeAfterData from '../components/BeforeAfterData';
import SavingsCalculator from '../components/SavingsCalculator';
import axios from 'axios';
import { 
  FileSpreadsheet, 
  ArrowRight, 
  Check,
  Phone,
  Mail,
  Users,
  Zap,
  Shield,
  ChevronRight,
  Scissors,
  Download,
  Sparkles,
  BarChart3,
  Activity,
  Upload,
  AlertTriangle,
  Clock,
  FileCheck,
  Percent
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [globalStats, setGlobalStats] = useState({
    rows_cleaned_this_month: 50000,
    avg_time_per_file_seconds: 45,
    avg_time_saved_per_1k_rows_minutes: 45,
    cells_updated_percent: 68
  });
  
  // Animated headline - cycle through time units
  const timeUnits = ['hours', 'days', 'weeks'];
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeIndex((prev) => (prev + 1) % timeUnits.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Fetch real stats from backend
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats/global`);
        if (response.data) {
          setGlobalStats({
            rows_cleaned_this_month: Math.max(response.data.rows_cleaned_this_month || 0, 50000),
            avg_time_per_file_seconds: response.data.avg_time_per_file_seconds || 45,
            avg_time_saved_per_1k_rows_minutes: Math.max(response.data.avg_time_saved_per_1k_rows_minutes || 0, 45),
            cells_updated_percent: Math.max(response.data.cells_updated_percent || 0, 68)
          });
        }
      } catch (err) {
        // Use default values on error
        console.log('Using default stats');
      }
    };
    fetchStats();
  }, []);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M+`;
    if (num >= 1000) return `${Math.floor(num / 1000)}K+`;
    return `${num}+`;
  };



  return (
    <div className="min-h-screen bg-white">
      {/* Reusable Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-28 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Copy */}
            <div>
              {/* Pain-focused badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-100 rounded-full text-red-700 text-sm font-medium mb-6">
                <AlertTriangle className="w-4 h-4" />
                Tired of cleaning Excel files manually?
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight tracking-tight">
                Data cleaning takes{' '}
                <span className="relative inline-block min-w-[120px] sm:min-w-[140px] lg:min-w-[180px]">
                  {timeUnits.map((unit, index) => (
                    <span
                      key={unit}
                      className={`absolute left-0 transition-all duration-500 text-red-500 whitespace-nowrap ${
                        index === currentTimeIndex
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 -translate-y-4'
                      }`}
                    >
                      {unit}
                    </span>
                  ))}
                  <span className="invisible">weeks</span>
                </span>
                <br />
                <span className="text-indigo-600">We do it in seconds.</span>
              </h1>
              
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                <strong>CRM managers, marketing ops & consultants:</strong> stop wasting hours 
                fixing phone formats, splitting names, and removing duplicates. 
                Clean your contact lists in seconds—not days.
              </p>
              
              {/* Key benefits */}
              <ul className="mt-6 space-y-2">
                {[
                  'Fix phone numbers to E.164 format (HubSpot, Salesforce ready)',
                  'Split "John Doe" into First Name + Last Name columns',
                  'Remove invalid emails & duplicates before import'
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/signup">
                  <Button 
                    className="h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all hover:-translate-y-0.5 w-full sm:w-auto"
                    data-testid="hero-cta-primary"
                  >
                    Start Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <p className="mt-4 text-sm text-slate-500 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Free to use • No credit card required
              </p>
            </div>
            
            {/* Right Column - Visual */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Main Card - Before/After Preview */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    <span className="ml-2 text-sm text-slate-500">contact_list.csv</span>
                  </div>
                  
                  {/* Before Data - Table Format */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Before</p>
                    <div className="bg-red-50 rounded-lg border border-red-100 overflow-hidden">
                      <table className="w-full text-[10px] font-mono">
                        <thead>
                          <tr className="bg-red-100/50 text-red-800">
                            <th className="px-2 py-1 text-left">Name</th>
                            <th className="px-2 py-1 text-left">Phone</th>
                            <th className="px-2 py-1 text-left">State</th>
                            <th className="px-2 py-1 text-left">Country</th>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-left">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="text-red-700">
                          <tr className="border-t border-red-100">
                            <td className="px-2 py-1">john DOE</td>
                            <td className="px-2 py-1">+1 (555) 123-4567</td>
                            <td className="px-2 py-1">california</td>
                            <td className="px-2 py-1">usa</td>
                            <td className="px-2 py-1">01/15/24</td>
                            <td className="px-2 py-1">$50000</td>
                          </tr>
                          <tr className="border-t border-red-100">
                            <td className="px-2 py-1">MARY smith</td>
                            <td className="px-2 py-1">555.987.6543</td>
                            <td className="px-2 py-1">NY</td>
                            <td className="px-2 py-1">United States</td>
                            <td className="px-2 py-1">2024-02-20</td>
                            <td className="px-2 py-1">1.2M</td>
                          </tr>
                          <tr className="border-t border-red-100">
                            <td className="px-2 py-1">bob JOHNSON</td>
                            <td className="px-2 py-1">15551234567</td>
                            <td className="px-2 py-1">Texas</td>
                            <td className="px-2 py-1">US</td>
                            <td className="px-2 py-1">March 5, 2024</td>
                            <td className="px-2 py-1">250k</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex justify-center my-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-indigo-600" />
                    </div>
                  </div>
                  
                  {/* After Data - Table Format */}
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">After</p>
                    <div className="bg-emerald-50 rounded-lg border border-emerald-100 overflow-hidden">
                      <table className="w-full text-[10px] font-mono">
                        <thead>
                          <tr className="bg-emerald-100/50 text-emerald-800">
                            <th className="px-2 py-1 text-left">First</th>
                            <th className="px-2 py-1 text-left">Last</th>
                            <th className="px-2 py-1 text-left">Phone</th>
                            <th className="px-2 py-1 text-left">State</th>
                            <th className="px-2 py-1 text-left">Country</th>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-left">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="text-emerald-700">
                          <tr className="border-t border-emerald-100">
                            <td className="px-2 py-1">John</td>
                            <td className="px-2 py-1">Doe</td>
                            <td className="px-2 py-1">+15551234567</td>
                            <td className="px-2 py-1">California</td>
                            <td className="px-2 py-1">United States</td>
                            <td className="px-2 py-1">2024-01-15</td>
                            <td className="px-2 py-1">50000</td>
                          </tr>
                          <tr className="border-t border-emerald-100">
                            <td className="px-2 py-1">Mary</td>
                            <td className="px-2 py-1">Smith</td>
                            <td className="px-2 py-1">+15559876543</td>
                            <td className="px-2 py-1">New York</td>
                            <td className="px-2 py-1">United States</td>
                            <td className="px-2 py-1">2024-02-20</td>
                            <td className="px-2 py-1">1200000</td>
                          </tr>
                          <tr className="border-t border-emerald-100">
                            <td className="px-2 py-1">Bob</td>
                            <td className="px-2 py-1">Johnson</td>
                            <td className="px-2 py-1">+15551234567</td>
                            <td className="px-2 py-1">Texas</td>
                            <td className="px-2 py-1">United States</td>
                            <td className="px-2 py-1">2024-03-05</td>
                            <td className="px-2 py-1">250000</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                  ✓ CRM Ready
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white border border-slate-200 text-slate-700 text-xs font-medium px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Cleaned in 2 seconds
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Compatibility Bar */}
      <section className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <p className="text-slate-400 text-sm font-medium">Works with your CRM:</p>
            <div className="flex items-center gap-6 sm:gap-8 flex-wrap justify-center">
              {/* HubSpot */}
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.984 2.21 2.21 0 00-4.42 0c0 .873.52 1.626 1.267 1.984V7.93a6.27 6.27 0 00-3.054 1.307L5.58 3.396a2.283 2.283 0 00.107-.683 2.29 2.29 0 10-2.29 2.29c.376 0 .73-.095 1.042-.258l7.56 5.79a6.27 6.27 0 00-.865 3.192c0 1.16.316 2.247.865 3.183l-2.37 1.82a2.213 2.213 0 00-1.401-.502 2.227 2.227 0 100 4.453 2.227 2.227 0 002.138-2.863l2.335-1.793a6.262 6.262 0 003.522 1.594v2.63a2.198 2.198 0 00-1.267 1.984 2.21 2.21 0 004.42 0c0-.872-.52-1.625-1.267-1.983v-2.631a6.288 6.288 0 005.245-6.194 6.29 6.29 0 00-5.245-6.195zm-.889 9.496a3.4 3.4 0 110-6.8 3.4 3.4 0 010 6.8z"/>
                </svg>
                <span className="text-sm font-medium">HubSpot</span>
              </div>
              {/* Salesforce */}
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.7 0 4.875 2.175 4.875 4.875s-2.175 4.875-4.875 4.875c-.39 0-.765-.045-1.125-.135-.63 1.56-2.175 2.655-3.975 2.655-1.05 0-2.01-.375-2.76-1.005-.72 1.215-2.04 2.025-3.555 2.025-2.31 0-4.185-1.875-4.185-4.185 0-.45.075-.885.21-1.29A3.725 3.725 0 010 10.62c0-2.07 1.68-3.75 3.75-3.75.51 0 1.005.105 1.455.3a4.18 4.18 0 014.8-1.755z"/>
                </svg>
                <span className="text-sm font-medium">Salesforce</span>
              </div>
              {/* Mailchimp */}
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.306 13.603c-.263-.138-.64-.127-.979.047-.224.115-.389.27-.476.414-.092.152-.1.265-.052.338.083.124.382.143.606.108.224-.035.584-.158.82-.355.107-.09.156-.21.139-.304a.256.256 0 00-.058-.248zm-1.9.69c-.101-.032-.246-.045-.373-.045a1.01 1.01 0 00-.523.139c-.139.082-.235.2-.29.36-.055.159-.052.336.012.492.072.176.222.329.417.424.216.105.443.117.642.033.166-.07.295-.2.378-.385.082-.185.079-.413-.009-.613-.073-.165-.203-.32-.355-.392l.1-.012zm3.65-1.652c-.23-.15-.523-.192-.726-.103-.228.099-.302.32-.166.492.136.172.495.27.76.173.266-.097.363-.412.132-.562z"/>
                </svg>
                <span className="text-sm font-medium">Mailchimp</span>
              </div>
              {/* Zoho */}
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.6c-5.302 0-9.6-4.298-9.6-9.6S6.698 2.4 12 2.4s9.6 4.298 9.6 9.6-4.298 9.6-9.6 9.6z"/>
                </svg>
                <span className="text-sm font-medium">Zoho</span>
              </div>
              {/* Any CRM */}
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <FileSpreadsheet className="w-5 h-5" />
                <span className="text-sm font-medium">Any CRM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar - Real Metrics from Backend */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8 border-y border-slate-100 bg-slate-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{formatNumber(globalStats.rows_cleaned_this_month)}</p>
                <p className="text-xs sm:text-sm text-slate-500">Rows cleaned this month</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{globalStats.avg_time_per_file_seconds}s</p>
                <p className="text-xs sm:text-sm text-slate-500">Avg. time to clean a file</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{globalStats.avg_time_saved_per_1k_rows_minutes}m</p>
                <p className="text-xs sm:text-sm text-slate-500">Avg. time saved / 1K rows</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Percent className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{globalStats.cells_updated_percent}%</p>
                <p className="text-xs sm:text-sm text-slate-500">Cells updated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-indigo-600 font-semibold mb-2">See the transformation</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
              From Messy to CRM-Ready
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Your chaotic spreadsheet becomes clean, standardized data 
              that imports perfectly—every time.
            </p>
          </div>
          
          <BeforeAfterData />
        </div>
      </section>

      {/* How It Works - Challenge Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          {/* Promise Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              The 5-Minute Challenge
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              In 3 Steps & Less Than 5 Minutes
            </h2>
            <p className="text-xl sm:text-2xl text-slate-700 font-medium">
              Clean a <span className="text-indigo-600 font-bold">25,000 rows</span> file with up to <span className="text-indigo-600 font-bold">15 columns</span>
            </p>
          </div>
          
          {/* Steps as Challenge */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                step: '01',
                icon: Upload,
                title: 'Upload',
                challenge: '30 seconds',
                desc: 'Drag & drop your messiest CSV or Excel file. We handle files up to 50MB.'
              },
              {
                step: '02',
                icon: Sparkles,
                title: 'Clean',
                challenge: '3 minutes',
                desc: 'Select your columns. Watch as we fix phones, split names, validate emails—all at once.'
              },
              {
                step: '03',
                icon: Download,
                title: 'Export',
                challenge: '30 seconds',
                desc: 'Download your CRM-ready file. Import directly to HubSpot, Salesforce, or any CRM.'
              }
            ].map((item, i) => (
              <div 
                key={i}
                className="relative bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8 hover:border-indigo-400 hover:shadow-xl transition-all group"
              >
                <span className="absolute -top-4 left-6 sm:left-8 text-5xl sm:text-6xl font-bold text-slate-100 group-hover:text-indigo-100 transition-colors">
                  {item.step}
                </span>
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors">
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{item.title}</h3>
                    <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      {item.challenge}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Challenge CTA */}
          <div className="mt-12 text-center">
            <p className="text-lg sm:text-xl text-slate-700 font-medium mb-6">
              Don't believe it's possible? <span className="text-indigo-600 font-bold">Prove us wrong.</span>
            </p>
            <Link to="/signup">
              <Button 
                className="h-14 px-10 text-lg bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all hover:-translate-y-0.5"
                data-testid="challenge-cta"
              >
                Take the 5-Minute Challenge
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-slate-500">
              Free to use • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* ROI Savings Calculator */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <SavingsCalculator 
            onGetStarted={() => navigate('/signup')}
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Stop Wasting Hours on Manual Data Work
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-8 sm:mb-10 max-w-2xl mx-auto">
            Join CRM managers and marketing ops teams who save hours every week.
          </p>
          
          <Link to="/signup">
            <Button 
              className="h-12 sm:h-14 px-8 sm:px-10 text-base sm:text-lg bg-white text-slate-900 hover:bg-slate-100 rounded-full shadow-lg hover:-translate-y-0.5 transition-all"
              data-testid="final-cta-btn"
            >
              Start Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-900">CRM Data Cleaner</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <Link to="/resources/blog" className="hover:text-slate-900">Blog</Link>
              <Link to="/features" className="hover:text-slate-900">Features</Link>
              <a href="mailto:hello@aidatacleaner.xyz" className="hover:text-slate-900">Contact</a>
            </div>
            
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} CRM Data Cleaner
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
